import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Save, X, Loader2 } from "lucide-react";

const inCls = "w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none";
const FIELDS = ["name", "surname", "company_name", "contact_number", "email", "tax_number", "registration_number", "bee_level", "district", "municipality", "town"];

const F = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

export const AdminSmmeEditor = ({ smme, meta, onSaved, onCancel }) => {
  const init = Object.fromEntries(FIELDS.map((k) => [k, smme[k] || ""]));
  init.sectors = smme.sectors?.length ? smme.sectors : (smme.sector ? smme.sector.split(",").map((s) => s.trim()).filter(Boolean) : []);
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value, ...(k === "district" ? { municipality: "" } : {}) });
  const txt = (k, label, type = "text") => (
    <F label={label}><input data-testid={`admin-edit-${k}`} type={type} value={form[k]} onChange={set(k)} className={inCls} /></F>
  );
  const sel = (k, label, opts) => (
    <F label={label}>
      <select data-testid={`admin-edit-${k}`} value={form[k]} onChange={set(k)} className={inCls}>
        <option value="">—</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </F>
  );
  const toggleSector = (s) => setForm({ ...form, sectors: form.sectors.includes(s) ? form.sectors.filter((x) => x !== s) : [...form.sectors, s] });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/smme/${smme.user_id}`, { ...form, sector: form.sectors.join(", ") });
      toast.success("SMME profile saved");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-smme-edit-form" className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        {txt("name", "Name")}{txt("surname", "Surname")}
        {txt("company_name", "Company")}{txt("email", "Email", "email")}
        {txt("contact_number", "Phone")}{sel("bee_level", "BEE Level", meta?.bee_levels || [])}
        {txt("tax_number", "Tax No.")}{txt("registration_number", "Reg No.")}
        {sel("district", "District", meta?.districts || [])}
        {sel("municipality", "Municipality", (meta?.municipalities || {})[form.district] || [])}
        {sel("town", "Town", meta?.towns || [])}
      </div>
      <F label={`Sectors (${form.sectors.length})`}>
        <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-md p-2 grid sm:grid-cols-2 gap-1">
          {(meta?.sectors || []).map((s) => (
            <label key={s} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid={`admin-edit-sector-${s}`} checked={form.sectors.includes(s)} onChange={() => toggleSector(s)} /> {s}
            </label>
          ))}
        </div>
      </F>
      <div className="flex justify-end gap-2 pt-1">
        <button data-testid="admin-edit-cancel" onClick={onCancel} className="flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"><X className="h-4 w-4" /> Cancel</button>
        <button data-testid="admin-edit-save" onClick={save} disabled={saving} className="flex items-center gap-1 px-4 py-2 rounded-md bg-[#00427A] text-white text-sm font-semibold hover:bg-[#003563] disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
      </div>
    </div>
  );
};
